import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { selectedBorder } from './selectedBorder.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'

/**
 * ParlaySTT — async action node that transcribes the last Voxy utterance via
 * a Parlay WebSocket and exposes the transcript on out-transcript.
 *
 * Typical wiring: onSpeechEnd → ParlaySTT → SendTextInput (with the node's
 * out-transcript pin feeding SendTextInput.in-text).
 *
 * Config:
 *   - endpoint: Parlay WS URL (e.g. wss://parlay.example/ws)
 *   - apiKey:   Parlay API key
 * Wired pins override the stored config.
 */

const HEADER_BG     = '#0a3028'
const HEADER_ACTIVE = '#10503a'
const ACCENT        = '#40d090'
const BORDER_SEL    = '#40d090'
const BORDER_IDLE   = '#0d4030'
const PIN_STRING    = '#c060e8'

export function ParlaySTTNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box',
    background: '#020a06', border: '1px solid #0d3020',
    color: '#60d090', padding: '4px 7px',
    borderRadius: 3, fontSize: 10,
    fontFamily: 'var(--m-mono)', outline: 'none',
  }

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder(BORDER_IDLE) : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 260, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      {/* Header */}
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🗣</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Parlay STT
        </span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      {/* ENDPOINT */}
      <div style={{ padding: '8px 12px 0 12px' }}>
        <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>ENDPOINT</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
          <Handle type="target" position={Position.Left} id="in-endpoint"
            style={{ background: PIN_STRING, width: 10, height: 10, borderRadius: '50%', border: '2px solid #80309866' }} />
          <input className={nodeControlClass()}
            value={data.endpoint ?? ''}
            onChange={e => data.onChange?.('endpoint', e.target.value)}
            onMouseDown={stop}
            placeholder="wss://parlay.example/ws"
            style={fieldStyle}
          />
        </div>
      </div>

      {/* API KEY */}
      <div style={{ padding: '6px 12px 0 12px' }}>
        <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>API KEY</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
          <Handle type="target" position={Position.Left} id="in-apiKey"
            style={{ background: PIN_STRING, width: 10, height: 10, borderRadius: '50%', border: '2px solid #80309866' }} />
          <input className={nodeControlClass()}
            value={data.apiKey ?? ''}
            onChange={e => data.onChange?.('apiKey', e.target.value)}
            onMouseDown={stop}
            placeholder="pk_xxx"
            style={fieldStyle}
          />
        </div>
      </div>

      {/* OUT: transcript */}
      <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', position: 'relative' }}>
        <span style={{ color: '#1a4030', fontSize: 9, letterSpacing: 0.5 }}>TRANSCRIPT</span>
        <Handle type="source" position={Position.Right} id="out-transcript"
          style={{ background: PIN_STRING, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${PIN_STRING}66` }} />
      </div>
    </div>
  )
}
